/**
 * Encoder de scene binario para electrical/trenches (formato .epcb v1).
 *
 * Objetivo: producir un ArrayBuffer compacto a partir del cadJson parseado +
 * la selección de capas del SelectLayersModal, para que al re-abrir el
 * pictograma el LayoutBuilder no tenga que descargar y re-parsear el DXF
 * (que puede pesar 200 MB+). El binario contiene solo las polilíneas/líneas
 * de las capas seleccionadas, lo mínimo para reconstruir BufferGeometries
 * de Three.js en zero-copy (`new Float32Array(buf, offset, len)`).
 *
 * Lo que NO incluye en v1:
 *   - TEXT/MTEXT (no se renderiza en este viewer).
 *   - INSERT/blocks (igual que en _AdvanceOverlayFilter, no expandimos v1).
 *   - ARC/CIRCLE (poco frecuentes en electrical/trenches; se pueden agregar
 *     en v2 con tessellation a posteriori).
 *   - Avance lineal — es lado cliente (localStorage) en esta fase.
 *
 * Formato (Little-Endian):
 *
 *   Header (24 B):
 *     magic[4]       = 'EPCB'
 *     version[4]     = uint32 = 1
 *     layerCount[4]  = uint32
 *     totalVerts[4]  = uint32      // estadística, ayuda al decoder a pre-allocate
 *     totalPolys[4]  = uint32
 *     reserved[4]    = uint32      // futuro: flags
 *
 *   Por capa (variable):
 *     codeLen[2]      = uint16
 *     code[codeLen]   = UTF-8       ("electrical_1", "trench_2", ...)
 *     colorRGB[4]     = uint32      // 0x00RRGGBB
 *     polyCount[4]    = uint32
 *     Por polyline:
 *       vertCount[4]      = uint32
 *       width[4]          = float32 // 0 → línea fina; >0 → ribbon (overlay clickeable)
 *       closedFlag[1]     = uint8   // 0 / 1
 *       vertices[8*vCnt]  = float32×2 (x,y interleaved, en coords del DXF)
 *
 * Nota sobre alineación: no padding entre campos — el decoder usa DataView
 * y respeta los offsets a mano. Sólo el bloque de vertices necesita
 * alineación a 4 B para zero-copy con Float32Array; el encoder garantiza
 * eso porque todos los campos previos son múltiplos de 4 excepto code y
 * closedFlag — se compensa con un padding a 4 B antes de cada vertices.
 */

const MAGIC      = 'EPCB'
const VERSION    = 1
const HEADER_LEN = 24

// Tipos que cuentan como polilínea (LINE se trata como polyline de 2 vértices).
const POLYLINE_TYPES = new Set(['LINE', 'LWPOLYLINE', 'POLYLINE'])

/**
 * Convierte cualquier entidad lineal soportada a la forma uniforme
 *   { vertices: [{x,y}, ...], width: number, closed: boolean }
 * Devuelve null si la entidad no es soportada o queda vacía tras saneo.
 */
function normalizeEntity(entity) {
  if (!entity || !POLYLINE_TYPES.has(entity.type)) return null

  let verts
  if (entity.type === 'LINE') {
    // LINE tiene .start/.end o .vertices según el parser. Cubrimos ambos.
    if (entity.vertices && entity.vertices.length >= 2) {
      verts = entity.vertices.slice(0, 2)
    } else if (entity.start && entity.end) {
      verts = [entity.start, entity.end]
    } else {
      return null
    }
  } else {
    verts = entity.vertices || []
  }
  if (!verts || verts.length < 2) return null

  // Filtrar vértices con NaN/Infinity — un solo malo NaN-iza todo el buffer
  // y rompe `computeBoundingSphere` del Three.js al usar la geometry.
  const clean = []
  for (const v of verts) {
    if (Number.isFinite(v?.x) && Number.isFinite(v?.y)) clean.push(v)
  }
  if (clean.length < 2) return null

  // Descartar polilíneas con cualquier vértice exactamente en el origen (0,0).
  // En diseños DXF reales el origen del mundo es un lugar arbitrario donde
  // raramente pasan polilíneas legítimas. Los XREF/INSERTs de AutoCAD suelen
  // dejar artefactos en la capa "0" con un vertex en (0,0) y otro en el punto
  // de inserción — invisibles en AutoCAD pero presentes en el cadJson, y al
  // pasarlos al .epcb crean "líneas hacia el origen" en el render del cliente.
  for (const v of clean) {
    if (v.x === 0 && v.y === 0) return null
  }

  // width: en LWPOLYLINE puede venir como constante en `entity.width` o
  // por-vértice en `startWidth`/`endWidth`. Tomamos el máximo (consistente
  // con _GetEntityMaxWidth del LayoutBuilder).
  let width = Number(entity.width) || 0
  for (const v of clean) {
    if (Number.isFinite(v.startWidth)) width = Math.max(width, v.startWidth)
    if (Number.isFinite(v.endWidth))   width = Math.max(width, v.endWidth)
  }
  if (!Number.isFinite(width)) width = 0

  return {
    vertices: clean,
    width,
    closed: !!(entity.shape || entity.closed),
  }
}

/**
 * Agrupa entidades soportadas por capa, respetando la selección.
 *
 * @param {Object} cadJson
 * @param {Set<string>|Array<string>} selectedDxfLayers - nombres DXF originales
 *   (no los codes normalizados electrical_n). Si null/undefined incluye todas.
 * @returns {Map<string, Array>} map dxfLayerName → [normalizedEntity, ...]
 */
function groupByLayer(cadJson, selectedDxfLayers) {
  const set  = selectedDxfLayers ? new Set(selectedDxfLayers) : null
  const out  = new Map()
  const raw  = cadJson?.entities
  if (!raw) return out
  const list = Array.isArray(raw) ? raw : Object.values(raw)

  for (const e of list) {
    if (!e?.layer) continue
    if (set && !set.has(e.layer)) continue
    const norm = normalizeEntity(e)
    if (!norm) continue
    if (!out.has(e.layer)) out.set(e.layer, [])
    out.get(e.layer).push(norm)
  }
  return out
}

/**
 * Empaqueta una cadena UTF-8 con su prefijo de longitud uint16.
 * Devuelve { bytes: Uint8Array, totalLen: number }.
 */
function encodeUtf8(str) {
  const enc = new TextEncoder()
  const bytes = enc.encode(str || '')
  if (bytes.length > 0xFFFF) {
    throw new Error('layer code too long (>65535 bytes): ' + str)
  }
  return bytes
}

/**
 * @param {Object} cadJson - DXF parseado (con .entities)
 * @param {Object} opts
 * @param {Array<{dxfLayer: string, code: string, color?: number}>} opts.layers
 *   Mapping de capa DXF original → code normalizado (ej. 'electrical_1') + color.
 *   El orden de este array define el orden en el binario.
 * @returns {ArrayBuffer}
 */
export function encodeScene(cadJson, { layers }) {
  if (!Array.isArray(layers) || layers.length === 0) {
    throw new Error('encodeScene: layers vacío')
  }

  // 1) Agrupar entidades por capa DXF (set = las que vienen en `layers`).
  const dxfLayerNames = layers.map(l => l.dxfLayer)
  const byLayer       = groupByLayer(cadJson, dxfLayerNames)

  // 2) Pre-calcular tamaño total y guardar buffers de code por capa.
  //    El padding de cada polyline depende del offset ABSOLUTO al llegar
  //    (no es constante 3) — por eso simulamos el cursor exactamente igual
  //    que en la pasada de escritura. Si esto se desincroniza con la
  //    pasada real, el assert final del encoder dispara.
  const codeBytesByLayer = layers.map(l => encodeUtf8(l.code))

  let totalVerts = 0
  let totalPolys = 0
  let cursor     = HEADER_LEN

  const perLayerEntities = layers.map(l => byLayer.get(l.dxfLayer) || [])

  for (let i = 0; i < layers.length; i++) {
    const entities = perLayerEntities[i]
    const codeBytes = codeBytesByLayer[i]

    // Header de capa: codeLen[2] + code[N] + colorRGB[4] + polyCount[4]
    cursor += 2 + codeBytes.length + 4 + 4

    for (const ent of entities) {
      // Header de polyline: vertCount[4] + width[4] + closedFlag[1] = 9 B
      cursor += 9
      // Padding hasta múltiplo de 4 (offset absoluto) para que el bloque
      // de vértices sea zero-copy a Float32Array en el decoder.
      cursor += (4 - (cursor % 4)) % 4
      cursor += ent.vertices.length * 8

      totalVerts += ent.vertices.length
      totalPolys += 1
    }
  }

  const buf  = new ArrayBuffer(cursor)
  const view = new DataView(buf)
  const u8   = new Uint8Array(buf)
  let off = 0

  // Header global
  u8[off++] = MAGIC.charCodeAt(0)
  u8[off++] = MAGIC.charCodeAt(1)
  u8[off++] = MAGIC.charCodeAt(2)
  u8[off++] = MAGIC.charCodeAt(3)
  view.setUint32(off, VERSION,        true); off += 4
  view.setUint32(off, layers.length,  true); off += 4
  view.setUint32(off, totalVerts,     true); off += 4
  view.setUint32(off, totalPolys,     true); off += 4
  view.setUint32(off, 0,              true); off += 4 // reserved

  // Body
  for (let i = 0; i < layers.length; i++) {
    const layer    = layers[i]
    const entities = perLayerEntities[i]
    const codeBytes = codeBytesByLayer[i]

    view.setUint16(off, codeBytes.length, true); off += 2
    u8.set(codeBytes, off); off += codeBytes.length
    view.setUint32(off, (layer.color >>> 0) & 0x00FFFFFF, true); off += 4
    view.setUint32(off, entities.length, true); off += 4

    for (const ent of entities) {
      view.setUint32(off, ent.vertices.length, true);  off += 4
      view.setFloat32(off, ent.width,          true);  off += 4
      view.setUint8(off, ent.closed ? 1 : 0);          off += 1
      // Padding hasta múltiplo de 4 para que Float32Array sea zero-copy.
      const pad = (4 - (off % 4)) % 4
      off += pad

      for (const v of ent.vertices) {
        view.setFloat32(off, v.x, true); off += 4
        view.setFloat32(off, v.y, true); off += 4
      }
    }
  }

  if (off !== buf.byteLength) {
    // Defensa: si esto se rompe es que el pre-cálculo de bodyLen se desfasó
    // del encoder real — error de programador, no del DXF.
    throw new Error(`encodeScene: offset mismatch — wrote ${off}, allocated ${buf.byteLength}`)
  }
  return buf
}

// Re-exportado para tests y por si el caller quiere inspeccionar el formato.
export const __INTERNALS__ = { MAGIC, VERSION, HEADER_LEN, normalizeEntity, groupByLayer }
